import React, { useMemo } from 'react';
import { Select, Space } from 'antd';
import { useSelector } from 'react-redux';
const { Option } = Select;

const ConditionsPromptsFilters = ({ filters, setFilters }) => {
  const { conditions, conditionsLoading, specialties, specialtiesLoading } =
    useSelector((state) => ({
      conditions: state?.conditionState?.conditionsDropdown,
      conditionsLoading: state?.conditionState?.conditionsLoading,
      specialties: state?.specialtyState?.getAllSpecialtiesList,
      specialtiesLoading: state?.specialtyState?.specialtiesLoading,
    }));
  const {
    prompts,
    promptsLoading,
    llms,
    llmsLoading,
    promptScreens,
    promptScreensLoading,
  } = useSelector((state) => state?.promptsState);
  // Filter conditions based on selected specialty
  const filteredConditions = useMemo(() => {
    if (!conditions || !Array.isArray(conditions)) return [];

    // If no specialty is selected, return all conditions
    if (!filters.specialty_id) {
      return conditions;
    }

    // Filter conditions by specialty_id
    return conditions.filter(
      (condition) => condition.specialty_id === filters.specialty_id
    );
  }, [conditions, filters.specialty_id]);

  return (
    <Space style={{ marginBottom: '16px' }} wrap>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="bg-white shadow-sm rounded-xl w-full sm:w-48 md:w-52 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Specialty"
            onChange={(value) => {
              // Clear condition filter when specialty changes
              setFilters({
                ...filters,
                specialty_id: value,
                condition_id: undefined,
              });
            }}
            value={filters.specialty_id}
            variant="borderless"
            allowClear
            showSearch
            loading={specialtiesLoading}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={specialties?.map((spec) => ({
              label: spec.specialty_name,
              value: spec.specialty_id,
            }))}
          />
        </div>

        <div className="bg-white shadow-sm rounded-xl w-full sm:w-48 md:w-52 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Condition"
            onChange={(value) =>
              setFilters({ ...filters, condition_id: value })
            }
            value={filters.condition_id}
            loading={conditionsLoading}
            variant="borderless"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filteredConditions?.map((cond) => ({
              label: cond.condition_name,
              value: cond.condition_id,
            }))}
          />
        </div>

        <div className="bg-white shadow-sm rounded-xl w-full sm:w-64 md:w-72 lg:w-80 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Prompt Screen"
            onChange={(value) =>
              setFilters({ ...filters, prompt_screens_id: value })
            }
            value={filters.prompt_screens_id}
            loading={promptScreensLoading}
            variant="borderless"
            allowClear
            showSearch
            optionLabelProp="label"
            filterOption={(input, option) => {
              const children = option.children;
              const textContent =
                children?.props?.children?.[0]?.props?.children || '';
              const descContent =
                children?.props?.children?.[1]?.props?.children || '';
              return (
                textContent.toLowerCase().includes(input.toLowerCase()) ||
                descContent.toLowerCase().includes(input.toLowerCase())
              );
            }}
          >
            {promptScreens?.prompt_screens?.map((screen) => (
              <Option
                key={screen.prompt_screens_id}
                value={screen.prompt_screens_id}
                label={screen.description || screen.prompt_screen}
              >
                <div className="flex flex-col">
                  <div className="font-medium">
                    {screen.description || screen.prompt_screen}
                  </div>
                  {screen.description && (
                    <div className="text-xs text-gray-500">
                      {screen.prompt_screen}
                    </div>
                  )}
                </div>
              </Option>
            ))}
          </Select>
        </div>

        <div className="flex items-center">
          <span
            onClick={() => {
              setFilters({ page: 1, per_page: 20, has_condition: true });
            }}
            className="text-sm cursor-pointer hover:underline whitespace-nowrap"
          >
            Clear
          </span>
        </div>
      </div>
    </Space>
  );
};

export default ConditionsPromptsFilters;
